import { useEffect, useState } from "react";

import {
  ratedItemsOf,
  type Assessment,
  type AssessmentId,
  type RatedItem,
} from "@thalamos/haven-common";

import api from "../../api/index.js";
import { countsOf } from "../FullHistory/fullHistory.js";
import { loadForm, type InstrumentForm } from "./AssessmentContext.js";
import { latestSubmission } from "./latestSubmission.js";

/** One of several assessments, as far as reading it and building its form has got. */
export type AssessmentReading =
  | { kind: "loading" }
  | { kind: "unreadable" }
  | { kind: "unavailable"; assessment: Assessment }
  | { kind: "read"; assessment: Assessment; form: InstrumentForm };

const readingOf = async (
  assessmentId: AssessmentId,
  formOf: (assessment: Assessment) => Promise<InstrumentForm | "unavailable">,
): Promise<AssessmentReading> => {
  const assessment = await api.assessment.byId(assessmentId);

  if (assessment === undefined) {
    return { kind: "unreadable" };
  }

  const form = await formOf(assessment);

  return form === "unavailable"
    ? { kind: "unavailable", assessment }
    : { kind: "read", assessment, form };
};

const sameIds = (
  left: readonly AssessmentId[],
  right: readonly AssessmentId[],
): boolean =>
  left.length === right.length &&
  left.every((assessmentId, index) => assessmentId === right[index]);

/**
 * Reads each of several assessments once, with the form of the instrument
 * version it pinned, built once for every assessment pinning that version.
 *
 * Read once rather than watched: what a finished assessment says does not
 * change. An assessment not yet read is absent from the map. A reading stays
 * in the map after its assessment stops being wanted; one still in flight when
 * that happens is dropped.
 */
export const useAssessmentReadings = (
  assessmentIds: readonly AssessmentId[],
): ReadonlyMap<AssessmentId, AssessmentReading> => {
  const [readings, setReadings] = useState<
    ReadonlyMap<AssessmentId, AssessmentReading>
  >(new Map());
  // Replaced only when the ids, or their order, differ, so a new array of the
  // same ids does not make the effect read them again.
  const [wanted, setWanted] = useState(assessmentIds);

  if (!sameIds(wanted, assessmentIds)) {
    setWanted(assessmentIds);
  }

  useEffect(() => {
    if (wanted.length === 0) {
      return;
    }

    let current = true;
    const forms = new Map<string, Promise<InstrumentForm | "unavailable">>();
    const formOf = ({ instrument }: Assessment) => {
      const known = forms.get(instrument.digest);

      if (known !== undefined) {
        return known;
      }

      const loading = loadForm(instrument.name, instrument.digest).catch(
        (): "unavailable" => "unavailable",
      );
      forms.set(instrument.digest, loading);
      return loading;
    };

    for (const assessmentId of wanted) {
      void readingOf(assessmentId, formOf)
        .catch((): AssessmentReading => ({ kind: "unreadable" }))
        .then((reading) => {
          if (current) {
            setReadings((before) => new Map(before).set(assessmentId, reading));
          }
        });
    }

    return () => {
      current = false;
    };
  }, [wanted]);

  return readings;
};

/**
 * Every item an assessment's latest submission rated, once it is read with
 * its form, or undefined where it is not read, has no submission, or its
 * instrument declares no kinds of domain.
 */
export const ratedOfReading = (
  reading: AssessmentReading | undefined,
): RatedItem[] | undefined => {
  if (reading?.kind !== "read") {
    return undefined;
  }

  const submission = latestSubmission(reading.assessment);

  return submission === undefined
    ? undefined
    : ratedItemsOf({
        schema: reading.form.schema,
        form: reading.form.form,
        responses: submission.responses,
      });
};

/** How many needs each assessment read has for focus, as its summary shows them. */
export const needsForFocusOf = (
  readings: ReadonlyMap<AssessmentId, AssessmentReading>,
): ReadonlyMap<AssessmentId, number> =>
  new Map(
    [...readings].flatMap(([assessmentId, reading]) => {
      const rated = ratedOfReading(reading);

      return rated === undefined
        ? []
        : [[assessmentId, countsOf(rated).needs] as const];
    }),
  );
